import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Loader2, Download, Trash2, ShieldAlert, Database } from 'lucide-react';
import { extractApiErrorMessage, networkErrorMessage } from '../lib/errors';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyDataPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  // "Armado" = o usuário digitou a palavra exigida. Governa o disabled, a guarda do
  // handler e o pulso do botão, para os três não poderem divergir.
  const deleteArmed = deleteConfirm.trim().toUpperCase() === 'EXCLUIR';

  const handleExportData = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/account/export`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error(await extractApiErrorMessage(res, 'Erro ao exportar dados'));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `meus-dados-watizap-${today}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Download iniciado.');
    } catch (error) {
      toast.error(networkErrorMessage(error, 'Não foi possível exportar seus dados.'));
    } finally {
      setExporting(false);
    }
  };

  const handleSelfDelete = async () => {
    if (!deleteArmed) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/account/self-delete`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ confirm: 'EXCLUIR' }),
      });
      if (!res.ok) {
        toast.error(await extractApiErrorMessage(res, 'Erro ao excluir conta'));
        setDeleting(false);
        return;
      }
      toast.success('Sua conta foi excluída.');
      setDeleteModalOpen(false);
      setTimeout(() => {
        logout();
        navigate('/', { replace: true });
      }, 800);
    } catch (error) {
      toast.error(networkErrorMessage(error, 'Não foi possível excluir sua conta.'));
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Card 1: Portabilidade */}
      <Card data-testid="my-data-card" className="w-fit max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
              <Database className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </div>
            <div>
              <CardTitle>Portabilidade de Dados</CardTitle>
              <CardDescription>
                Baixe uma cópia completa dos seus dados pessoais armazenados na plataforma.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4 max-w-2xl">
            {/* Export */}
            <div className="border rounded-lg p-4 flex flex-col">
              <h4 className="font-semibold text-base mb-1 flex items-center gap-2">
                <Download className="w-4 h-4" />
                Exportar meus dados
              </h4>
              <p className="text-sm text-muted-foreground mb-4 flex-1">
                Baixe um arquivo <strong>.zip</strong> com seu perfil, assinatura, contatos,
                grupos, mensagens, campanhas, consentimentos e usuários. Direito previsto
                no art. 18, V da LGPD (portabilidade).
              </p>
              <Button
                variant="outline"
                onClick={handleExportData}
                disabled={exporting}
                data-testid="export-data-button"
                className="self-start"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {exporting ? 'Gerando arquivo...' : 'Exportar meus dados'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card 2: Zona de Perigo */}
      <Card data-testid="danger-zone-card" className="w-fit max-w-2xl border-red-200 dark:border-red-900/40">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-950/40">
              <ShieldAlert className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <CardTitle className="text-red-700 dark:text-red-400">Zona de Perigo</CardTitle>
              <CardDescription>
                A ação abaixo é permanente e <strong>irreversível</strong>. Prossiga apenas se tiver certeza.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4 max-w-2xl">
            {/* Delete */}
            <div className="border border-red-200 dark:border-red-900/40 rounded-lg p-4 flex flex-col bg-red-50/40 dark:bg-red-950/10">
              <h4 className="font-semibold text-base mb-1 flex items-center gap-2 text-red-700 dark:text-red-400">
                <ShieldAlert className="w-4 h-4" />
                Excluir minha conta
              </h4>
              <p className="text-sm text-muted-foreground mb-4 flex-1">
                Cancela sua assinatura imediatamente e remove permanentemente
                todos os seus dados da plataforma. <strong>Esta ação é irreversível.</strong>
              </p>
              {/* Esmaecido em repouso e sólido no hover: em uma danger zone, a intensidade
                  visual tem que crescer junto com o compromisso. O vermelho cheio fica
                  reservado para a confirmação final, dentro do modal. */}
              <Button
                variant="outline"
                onClick={() => { setDeleteConfirm(''); setDeleteModalOpen(true); }}
                data-testid="delete-account-button"
                className="self-start bg-transparent border-red-300 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-700 dark:hover:text-white dark:hover:border-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir minha conta agora
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal: Confirm Self-Delete */}
      <Dialog open={deleteModalOpen} onOpenChange={(open) => !deleting && setDeleteModalOpen(open)}>
        <DialogContent className="sm:max-w-xl" data-testid="delete-account-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-red-700">
              <ShieldAlert className="w-5 h-5" />
              Excluir minha conta permanentemente
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2 text-left text-base text-foreground/80">
              <span className="block">
                Esta ação é <strong>permanente e irreversível</strong>. Ao confirmar, iremos:
              </span>
              <span className="block">
                <ul className="list-disc pl-5 space-y-1 mt-2 text-base">
                  <li>Cancelar sua assinatura imediatamente;</li>
                  <li>Apagar todas as suas mensagens, contatos, grupos e campanhas;</li>
                  <li>Apagar todos os arquivos anexados;</li>
                  <li>Encerrar sua sessão e remover seu acesso.</li>
                </ul>
              </span>
              <span className="block mt-3 text-base text-foreground/80">
                Recomendamos exportar seus dados antes de prosseguir.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <Label htmlFor="delete-confirm" className="text-base">
              Para confirmar, digite <strong>EXCLUIR</strong> abaixo:
            </Label>
            <Input
              id="delete-confirm"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value.toUpperCase())}
              placeholder="EXCLUIR"
              autoComplete="off"
              disabled={deleting}
              data-testid="delete-confirm-input"
              className="uppercase placeholder:normal-case"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              disabled={deleting}
              data-testid="delete-cancel-button"
            >
              Cancelar
            </Button>
            {/* Único ponto de movimento da jornada: pulsa enquanto estiver armado,
                confirmando que a palavra digitada foi aceita. */}
            <Button
              variant="destructive"
              onClick={handleSelfDelete}
              disabled={deleting || !deleteArmed}
              data-testid="delete-confirm-button"
              className={deleteArmed ? 'btn-armed-pulse' : ''}
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Excluir agora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
